import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select, ConfigProvider } from 'antd'
import { Picker } from 'antd-mobile'
import dayjs from 'dayjs'
import { BorderedPageLayout } from '../BorderedPageLayout'
import { BackButton } from '../../components/BackButton'
import useTheme from '../../hooks/useAppColor'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { getFilials } from '../../features/products/productSlice'
import { FilialApi, ScheduleSlotApi, UsiDoctorApi } from '../../types/api/utils.api.types'
import { handleTokenRefreshedRequest } from '../../utils/handleThunkAuth'
import { UtilsApi } from '../../http/api/utils.api'
import { PlaceIcon } from '../../icons'
import { YellowButton } from '../../components/YellowButton'
import { setUsiMeta } from '../../features/order/orderSlice'

type FilialOption = {
    label: string
    value: string
    original: FilialApi
}

type DoctorOption = {
    label: string
    value: string
    original: UsiDoctorApi
}

export const OrderUSI = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const getTheme = useTheme()
    const filials = useAppSelector(state => state.products.filials)
    const [selectedFilial, setSelectedFilial] = useState<FilialApi | null>(null)
    const [doctors, setDoctors] = useState<UsiDoctorApi[]>([])
    const [selectedDoctor, setSelectedDoctor] = useState<UsiDoctorApi | null>(null)
    const [doctorsLoading, setDoctorsLoading] = useState(false)
    const [visitDate, setVisitDate] = useState("")
    const [pickerVisible, setPickerVisible] = useState(false)
    const [pickerValue, setPickerValue] = useState<(string | number | null)[]>([])
    const [schedule, setSchedule] = useState<ScheduleSlotApi[]>([])
    const [scheduleLoading, setScheduleLoading] = useState(false)
    const [selectedSlot, setSelectedSlot] = useState<ScheduleSlotApi | null>(null)

    const MONTH_NAMES = [
        'янв.', 'фев.', 'мар.', 'апр.', 'май', 'июн.',
        'июл.', 'авг.', 'сен.', 'окт.', 'ноя.', 'дек.'
    ]

    const formatDateForBackend = (value: string) => {
        if (!value) {
            return ""
        }

        const [year, month, day] = value.split("-")
        return `${day}.${month}.${year}`
    }
    
    const disabled = !selectedFilial || !selectedDoctor || !visitDate || !selectedSlot

    const handleGoToOrder = () => {
        if (!selectedFilial || !selectedDoctor || !visitDate || !selectedSlot) {
            return
        }

        dispatch(setUsiMeta({
            filial: selectedFilial.name,
            doctor: `${selectedDoctor.last_name} ${selectedDoctor.first_name} ${selectedDoctor.middle_name}`,
            doc_id: selectedDoctor.id,
            date: visitDate,
            time: selectedSlot.start_time
        }))
        navigate("/order/patient")
    }

    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month, 0).getDate()
    }

    const buildDateColumns = (yearValue?: string | null, monthValue?: string | null) => {
        const now = dayjs()
        const startYear = now.year() - 1
        const endYear = now.year() + 2

        const years = Array.from({ length: endYear - startYear + 1 }, (_, idx) => {
            const value = String(startYear + idx)
            return { label: `${value} г.`, value }
        })

        const months = Array.from({ length: 12 }, (_, idx) => {
            const month = idx + 1
            const value = String(month).padStart(2, '0')
            return { label: MONTH_NAMES[idx], value }
        })

        const safeYear = Number(yearValue || now.format('YYYY'))
        const safeMonth = Number(monthValue || now.format('MM'))
        const maxDays = getDaysInMonth(safeYear, safeMonth)

        const days = Array.from({ length: maxDays }, (_, idx) => {
            const day = idx + 1
            const value = String(day).padStart(2, '0')
            return { label: value, value }
        })

        return [
            days,
            months,
            years,
        ]
    }

    useEffect(() => {
        if (!filials.length || (filials.length > 5)) {
            dispatch(getFilials({"type": 0}))
        }
    }, [dispatch, filials.length])

    type GroupedFilials = {
        [city: string]: FilialOption[]
    }


    const groupFilialsByCity = (items: FilialApi[]) => {
        const grouped: GroupedFilials = {}

        items.forEach((filial) => {
            if (!grouped[filial.city]) {
                grouped[filial.city] = []
            }

            grouped[filial.city].push({
                label: `${filial.address}`, // Убираем город из названия, так как он будет в заголовке группы
                value: filial.id.toString(),
                original: filial
            })
        })

        return Object.keys(grouped).map(city => ({
            label: <h1 style={{fontSize:18, fontWeight: 800}}>{city}</h1>,
            options: grouped[city]
        }))
    }

    const handleSelectFilial = (_value: string, option?: FilialOption | FilialOption[]) => {
        if (!option || Array.isArray(option)) {
            return
        }

        setSelectedFilial(option.original)
        setSelectedDoctor(null)
        setVisitDate("")
        setSchedule([])
        setSelectedSlot(null)
    }

useEffect(() => {
    if (!selectedFilial) {
        setDoctors([])
        setSelectedDoctor(null)
        return
    }

    const loadDoctors = async () => {
        setDoctorsLoading(true)
        try {
            
            const res = await handleTokenRefreshedRequest(null, UtilsApi.GetUsiDocs, { 
                room_id: selectedFilial.id 
            })
            
            if (Array.isArray(res.data)) {
                
                const filteredDoctors = res.data.filter(
                    (doc: UsiDoctorApi) => doc.id_room === selectedFilial.id && doc.active
                )
                setDoctors(filteredDoctors)
            } else {
                setDoctors([])
            }
        } catch (error) {
            setDoctors([])
        } finally {
            setDoctorsLoading(false)
        }
    }

    loadDoctors()
}, [selectedFilial])

    const doctorOptions: DoctorOption[] = doctors.map((doctor) => ({
        label: `${doctor.last_name} ${doctor.first_name} ${doctor.middle_name}\n (${doctor.flong} мин.)`,
        value: doctor.id.toString(),
        original: doctor
    }))

    const handleSelectDoctor = (_value: string, option?: DoctorOption | DoctorOption[]) => {
        if (!option || Array.isArray(option)) {
            return
        }

        setSelectedDoctor(option.original)
        setVisitDate("")
        setSchedule([])
        setSelectedSlot(null)
    }

    useEffect(() => {
        if (!selectedDoctor || !visitDate) {
            setSchedule([])
            setSelectedSlot(null)
            return
        }

        const loadSchedule = async () => {
            setScheduleLoading(true)
            try {
                const res = await handleTokenRefreshedRequest(
                    null,
                    UtilsApi.GetSchedule,
                    { doc_id: selectedDoctor.id, date: formatDateForBackend(visitDate) }
                )
                setSchedule(Array.isArray(res.data) ? res.data : [])
            } catch (error) {
                setSchedule([])
            } finally {
                setScheduleLoading(false)
            }
        }

        loadSchedule()
    }, [selectedDoctor, visitDate])

    const dayOfWeek = visitDate
        ? new Date(`${visitDate}T00:00:00`).toLocaleDateString('ru-RU', { weekday: 'long' })
        : ""

    return (
        <BorderedPageLayout
            contentClassName={"f-column-betw"}
            top={
                <div className='f-row-betw'>
                    <BackButton onClick={() => navigate("/")} />
                    <h2 className={`${getTheme("", "c-white")} title fw-6`}>УЗИ</h2>
                    <div></div>
                </div>
            }>
            <div className="f-column gap-20">
                <h2 className={`title ${getTheme("", "c-white")}`}>Выберите филиал</h2>
                    <ConfigProvider direction="ltr" theme={{
                        components: {
                            Select: {
                                activeBorderColor: "#FFCF00",
                                activeOutlineColor: "#FFCF00",
                                hoverBorderColor: "#4D4D4D",
                                optionHeight: 25,
                                optionFontSize: 16,
                                optionSelectedFontWeight: 300,
                                optionSelectedBg: "#FFDA75",
                            },
                        },
                    }}>
                    <Select
                        optionLabelProp="label"
                        placeholder="Выберите филиал"
                        size='large'
                        value={selectedFilial?.id.toString()}
                        onChange={handleSelectFilial}
                        options={groupFilialsByCity(filials) as any}
                        style={{ width: "100%", whiteSpace: 'normal' }}
                    />
                </ConfigProvider>
                {selectedFilial ? (
                    <div className={`pd-10 search-block ${getTheme("", "app-bg-dark")}`}>
                        <p className={`fz-m ${getTheme("c-dark", "c-white")}`}>{selectedFilial.name}</p>
                        <p className={`fz-s ${getTheme("c-dark", "c-white")}`}>{selectedFilial.address}</p>
                    </div>
                ) : null}
                {selectedFilial ? (
                    <>
                        <h2 className={`title ${getTheme("", "c-white")}`}>Выберите врача</h2>
                        <ConfigProvider direction="ltr" theme={{
                            components: {
                                Select: {
                                    activeBorderColor: "#FFCF00",
                                    activeOutlineColor: "#FFCF00",
                                    hoverBorderColor: "#4D4D4D",
                                    optionHeight: 25,
                                    optionFontSize: 16,
                                    optionSelectedFontWeight: 300,
                                    optionSelectedBg: "#FFDA75",
                                },
                            },
                        }}>
                            <Select
                                optionLabelProp="label"
                                placeholder={doctorsLoading ? "Загрузка врачей..." : "Выберите врача"}
                                size='large'
                                value={selectedDoctor?.id.toString()}
                                onChange={handleSelectDoctor}
                                options={doctorOptions}
                                loading={doctorsLoading}
                                disabled={doctorsLoading || doctorOptions.length === 0}
                                style={{ width: "100%", whiteSpace: 'normal' }}
                            />
                        </ConfigProvider>
                    </>
                ) : null}
                {selectedDoctor ? (
                    <>
                        <h2 className={`title ${getTheme("", "c-white")}`}>Выберите дату</h2>
                        <div
                            onClick={() => {
                                const current = visitDate ? dayjs(visitDate) : dayjs()
                                setPickerValue([
                                    current.format('DD'),
                                    current.format('MM'),
                                    current.format('YYYY')
                                ])
                                setPickerVisible(true)
                            }}
                            className={`w-100p fz-m pd-10 search-block d-f al-center f-row-betw ${getTheme("", "app-bg-dark c-white")}`}
                            style={{ cursor: "pointer" }}
                        >
                            <span>{visitDate ? dayjs(visitDate).format("DD.MM.YYYY") : "Нажмите, чтобы выбрать дату"}</span>
                            <PlaceIcon width={15} height={15} />
                        </div>
                        <Picker
                            visible={pickerVisible}
                            value={pickerValue}
                            columns={buildDateColumns(
                                pickerValue[2] !== null && pickerValue[2] !== undefined ? String(pickerValue[2]) : null,
                                pickerValue[1] !== null && pickerValue[1] !== undefined ? String(pickerValue[1]) : null
                            )}
                            onSelect={(val) => setPickerValue(val)}
                            onClose={() => setPickerVisible(false)}
                            onConfirm={(val) => {
                                const [day, month, year] = val as string[]
                                if (day && month && year) {
                                    setVisitDate(`${year}-${month}-${day}`)
                                    setSelectedSlot(null)
                                }
                            }}
                            confirmText="Выбрать"
                            cancelText="Отмена"
                            title="Дата визита"
                        />
                        {dayOfWeek ? (
                            <p className={`fz-s ${getTheme("c-dark", "c-white")}`}>
                                День недели: {dayOfWeek}
                            </p>
                        ) : null}
                    </>
                ) : null}
                {selectedDoctor && visitDate ? (
                    <>
                        <h2 className={`title ${getTheme("", "c-white")}`}>Выберите время</h2>
                        {scheduleLoading ? (
                            <p className={`fz-s ${getTheme("c-dark", "c-white")}`}>Загрузка слотов...</p>
                        ) : (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                    gap: 10
                                }}
                            >
                                <button style={{ display: "none" }}></button>

                                {schedule.map((slot) => {
                                    const isBusy = !!slot.isBusy
                                    const isSelected = selectedSlot?.start_time === slot.start_time && selectedSlot?.end_time === slot.end_time
                                    
                                    return (
                                        <button
                                            key={`${slot.start_time}-${slot.end_time}`}
                                            type="button"
                                            disabled={isBusy}
                                            onClick={() => setSelectedSlot(slot)}
                                            className={`pd-10 search-block ${getTheme("", "app-bg-dark c-white")}`}
                                            style={{
                                                opacity: isBusy ? 0.45 : 1,
                                                border: isSelected ? "2px solid #FFCF00" : "2px solid #bebebe",
                                                textAlign: "center",
                                                transition: "all 0.3s ease",
                                                borderBottom: "5px 5px 5px solid red"
                                            }}
                                        >
                                            {slot.start_time} - {slot.end_time}
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </>
                ) : null}

                <div className="f-column gap-5">
                    <YellowButton onClick={handleGoToOrder} disabled={disabled}>Далее</YellowButton>
                </div>
            </div>
        </BorderedPageLayout>
    )
}
